import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { FRIENDS_CHANGED_EVENT } from "@/lib/friends";
import Avatar from "@/components/Avatar";
import { ChatCircleDots } from "@phosphor-icons/react";

function when(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  const days = (now - d) / 86400000;
  if (days < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}

// Your direct messages with friends, latest first (/messages). Opened from
// "Messages" in the profile-picture menu.
export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/friends/conversations");
      setRows(data.conversations || []);
    } catch {
      setRows((r) => r || []);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState !== "hidden" && load(), 15000);
    window.addEventListener(FRIENDS_CHANGED_EVENT, load);
    return () => {
      clearInterval(t);
      window.removeEventListener(FRIENDS_CHANGED_EVENT, load);
    };
  }, [load]);

  return (
    <main className="max-w-2xl mx-auto px-4 md:px-6 py-6 md:py-10" data-testid="messages-page">
      <h1 className="font-display text-3xl font-black tracking-tight mb-5">Messages</h1>
      {rows === null ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-surface border border-border rounded-2xl p-8 text-center">
          <ChatCircleDots size={32} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground mb-4">
            Add friends to message them. Find a player's profile and press Add friend.
          </p>
          <button
            type="button"
            onClick={() => navigate("/profile#friends")}
            className="h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider bg-brand text-brand-foreground hover:bg-brand-hover"
          >
            Find friends
          </button>
        </div>
      ) : (
        <ul className="bg-surface border border-border rounded-2xl overflow-hidden" data-testid="messages-list">
          {rows.map((c) => {
            const mine = c.last?.from === user?.id;
            return (
              <li key={c.id} className="border-t border-border first:border-t-0">
                <button
                  type="button"
                  onClick={() => navigate(`/friends/${c.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                  data-testid={`conversation-${c.username}`}
                >
                  <Avatar userId={c.id} username={c.username} version={c.avatar_v ?? null} className="w-11 h-11 text-base" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className={`truncate ${c.unread ? "font-bold" : "font-semibold"}`}>{c.username}</span>
                      <span className="ml-auto flex-none text-xs text-muted-foreground">{when(c.last?.ts)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`truncate text-sm ${c.unread ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                        {c.last ? `${mine ? "You: " : ""}${c.last.text}` : "Say hi"}
                      </span>
                      {c.unread > 0 && (
                        <span className="ml-auto flex-none min-w-[1.25rem] h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold leading-5 text-center">
                          {c.unread > 9 ? "9+" : c.unread}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
